import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

/**
 * 認証が必要なルートを保護するコンポーネント
 * 
 * 意図：
 * - 未認証ユーザーのアクセスを制限
 * - ログインページへの自動リダイレクト
 * - ログイン後の元ページへの復帰
 * 
 * 注意点：
 * - ローディング中の表示
 * - リダイレクトループの防止
 * - 認証状態の正確な判定
 * 
 * 改善提案：
 * - ロールベースのアクセス制御
 * - カスタムリダイレクトパス
 * - アクセス拒否ページ
 */

interface ProtectedRouteProps {
  allowedRoles?: Array<'admin' | 'doctor' | 'operator'>;
  redirectTo?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  allowedRoles,
  redirectTo = '/login',
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  // 認証状態を確認中
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  // 未認証の場合はログインページへリダイレクト
  if (!isAuthenticated) {
    // 現在のパスを記録して、ログイン後に戻れるようにする
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // ロールベースのアクセス制御
  if (allowedRoles && user && !allowedRoles.includes(user.role)) {
    // アクセス権限がない場合はダッシュボードへリダイレクト
    return <Navigate to="/dashboard" replace />;
  }

  // 認証済みの場合は子コンポーネントを表示
  return <Outlet />;
};

/**
 * 管理者のみアクセス可能なルート
 */
export const AdminRoute: React.FC = () => {
  return <ProtectedRoute allowedRoles={['admin']} />;
};

/**
 * 医師のみアクセス可能なルート
 */
export const DoctorRoute: React.FC = () => {
  return <ProtectedRoute allowedRoles={['doctor']} />;
};

/**
 * オペレーターのみアクセス可能なルート
 */
export const OperatorRoute: React.FC = () => {
  return <ProtectedRoute allowedRoles={['operator']} />;
};

/**
 * 管理者とオペレーターがアクセス可能なルート
 */
export const AdminOrOperatorRoute: React.FC = () => {
  return <ProtectedRoute allowedRoles={['admin', 'operator']} />;
};

/**
 * すべての認証済みユーザーがアクセス可能なルート
 */
export const AuthenticatedRoute: React.FC = () => {
  return <ProtectedRoute />;
};

export default ProtectedRoute;